/**
 * An address input that suggests as you type — Mapbox, through our own backend
 * (GET /api/address/autocomplete), so the browser never holds a Mapbox token.
 *
 * Used by the user-comps form only. Three promises:
 *
 *   1. It is a plain controlled input. Whatever the user types is the value, always —
 *      a rural or off-market address Mapbox has never heard of goes through exactly as
 *      typed. Suggestions are an overlay, never a gate.
 *   2. One request per pause, not per keystroke: DEBOUNCE_MS after the last key, and
 *      only from MIN_CHARS on. A reply that arrives after a newer query was sent is
 *      dropped, so a slow answer for "187 Le" cannot overwrite the list for "187 Leroy".
 *   3. Choosing a suggestion fills the address and nothing else. Price, size, date stay
 *      the user's.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { analysisAPI } from '../api';

export const DEBOUNCE_MS = 400;
export const MIN_CHARS = 3;

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  lang = 'en',
  placeholder,
  className,
  inputProps = {},
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);            // the number of the latest query sent
  const chosen = useRef(false);     // a pick just happened: do not re-query its own text
  const rootRef = useRef(null);

  // Query on pause, not on keystroke.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = (value || '').trim();
    if (chosen.current) { chosen.current = false; return undefined; }
    if (q.length < MIN_CHARS) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    timer.current = setTimeout(async () => {
      const mine = ++seq.current;
      setLoading(true);
      try {
        const r = await analysisAPI.autocomplete(q);
        if (mine !== seq.current) return;           // a newer query is in flight
        const list = (r?.data?.suggestions || []).filter((s) => s && s.full_address);
        setSuggestions(list);
        setOpen(list.length > 0);
        setActive(-1);
      } catch (e) {
        if (mine === seq.current) { setSuggestions([]); setOpen(false); }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [value]);

  // Click outside closes the list; the text stays.
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (s) => {
    chosen.current = true;
    seq.current += 1;                 // anything still in flight is stale now
    setOpen(false);
    setSuggestions([]);
    setActive(-1);
    onChange(s.full_address);
    if (onSelect) onSelect(s);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        {...inputProps}
      />
      {loading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400" data-testid="address-autocomplete-loading">
          {lang === 'en' ? 'searching…' : 'buscando…'}
        </span>
      )}
      {open && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg text-sm"
          role="listbox"
          data-testid="address-suggestions"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.full_address}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-start gap-2 px-3 py-2 cursor-pointer ${i === active ? 'bg-blue-50 text-slate-900' : 'text-slate-700 hover:bg-slate-50'}`}
              data-testid="address-suggestion"
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{s.full_address}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
